/*
Copyright (C) 2026  Tech9IQ
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * Sign-in from Tech9IQ (docs/swift-sso.md): the Ops Console or the customer
 * portal opens /login/tech9iq#at=<token>&back=/admin/…; this reads the
 * fragment (which browsers never send to a server), takes it out of the
 * address bar, and posts it to this Swift server, which signs the person in
 * and sets the same cookies the sign-in form does.
 */

import configLoader from "@src/utils/Config";

export type SsoOptions = { enabled: boolean; localLogin: boolean; startUrl: string };

export type Handoff = { token: string; back: string | null };

/** The token and the page to go back to, from `#at=…&back=…`; null without a token. */
export function readHandoff(hash: string): Handoff | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const token = params.get("at");
  if (!token) return null;
  const back = params.get("back");
  return { token, back: back && /^\/admin\/[^/\\]/.test(back) && !back.includes("\\") ? back : null };
}

/** The sign-in options /api/config reports (sso/routes.ts applySsoConfig). */
export function ssoOptions(): SsoOptions {
  const cfg: any = configLoader.config || {};
  const sso = cfg.tech9iqSso || {};
  return {
    enabled: Boolean(sso.enabled),
    localLogin: sso.localLogin !== false,
    startUrl: typeof sso.startUrl === "string" ? sso.startUrl : "/api/sso/start",
  };
}

export class SsoError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

class SsoSource {
  async exchange(handoff: Handoff): Promise<{ next: string }> {
    const res = await fetch("/api/sso/handoff", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token: handoff.token, back: handoff.back }),
    });
    const body: any = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new SsoError(res.status, (body && body.error && body.error.message) || "Signing in from Tech9IQ did not go through.");
    }
    // Only a path on this site.
    const next = typeof body.next === "string" && body.next.startsWith("/") && !body.next.startsWith("//") ? body.next : "/";
    return { next };
  }
}

export default new SsoSource();
